// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, Checkbox, Modal, Spinner, TextArea, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../../../api/proposalApi';
import { relationshipCashflowApi, type Scenario } from '../../../../../api/relationshipCashflowApi';
import type {
  BreakthroughLifestyleSectionContent,
  BreakthroughProposalDetails,
  Proposal,
  ProposalSection,
} from '../../../../../types/proposal';
import { formatCurrency } from '../../../proposalDisplay';
import { useAiBlockDraft } from '../../useAiBlockDraft';
import { AiGenerateRow } from '../AiGenerateRow';

export type SectionLifestyleRetirementProps = Readonly<{
  proposalId: string;
  /** The relationship — saved forecasts are profile-scoped. */
  profileId?: string;
  proposal: Proposal;
  section: ProposalSection;
  onSaved: (updated: ProposalSection) => void;
  /**
   * Persists a report-level detail change (here: the embedded scenario ids)
   * — the caller merges it onto the full `BreakthroughProposalDetails`
   * object and PUTs the proposal (a partial PUT would null out the other
   * fields server-side).
   */
  onUpdateDetails: (partial: Partial<BreakthroughProposalDetails>) => Promise<void>;
  /**
   * Renders the "Client view" preview (BR-P5, PRD §5 / RQ-BR8): the
   * per-scenario chart(s) + pension-structures summary are already
   * read-only and render unchanged; the "choose forecasts" picker and the
   * narrative editor + Save button are hidden. Defaults to false (advisor
   * editable builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function readContent(section: ProposalSection): BreakthroughLifestyleSectionContent {
  return (section.content as BreakthroughLifestyleSectionContent | null) ?? {};
}

function readDetails(proposal: Proposal): BreakthroughProposalDetails {
  return (proposal.details as BreakthroughProposalDetails | null) ?? {};
}

/**
 * Coerce the REPORT_BUILDER reply into the plain-text narrative this section
 * stores. The prompt is asked for `{narrative}`, but tolerate an `{html}` or
 * `{bullets}` reply too (strip tags / join bullets) so a differently-shaped
 * reply still applies rather than erroring. Returns null when nothing usable
 * is present (surfaces as the inline parse error).
 */
function replyToNarrative(parsed: Record<string, unknown>): string | null {
  if (typeof parsed.narrative === 'string' && parsed.narrative.trim().length > 0) {
    return parsed.narrative.trim();
  }
  if (typeof parsed.html === 'string' && parsed.html.trim().length > 0) {
    const text = parsed.html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > 0 ? text : null;
  }
  if (Array.isArray(parsed.bullets)) {
    const items = parsed.bullets
      .filter((b): b is string => typeof b === 'string' && b.trim().length > 0)
      .map((b) => `• ${b.trim()}`);
    if (items.length > 0) return items.join('\n');
  }
  return null;
}

/** Series colours — theme tokens read by recharts' SVG (mirrors PlanCashflowSection). */
const SERIES_COLORS = [
  'var(--color-purple-primary)',
  'var(--color-success-text)',
  'var(--color-error-text)',
  'var(--color-grey-03)',
];

/**
 * BR_LIFESTYLE_RETIREMENT (PRD §4 row 6 / RQ-BR4) — the advisor embeds 1..N
 * saved cashflow forecasts (persisted on the proposal's detail row via
 * `onUpdateDetails`); the server re-derives each scenario's frozen
 * income-source stacked series on every read while DRAFT, rendered here as a
 * small per-scenario preview chart. A free-text narrative is advisor-owned.
 */
export function SectionLifestyleRetirement({
  proposalId,
  profileId,
  proposal,
  section,
  onSaved,
  onUpdateDetails,
  readOnly = false,
}: SectionLifestyleRetirementProps) {
  const { showToast } = useToast();
  const content = readContent(section);
  const scenarios = useMemo(() => content.scenarios ?? [], [content.scenarios]);

  const [narrative, setNarrative] = useState(content.narrative ?? '');
  const [narrativeDirty, setNarrativeDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [allScenarios, setAllScenarios] = useState<Scenario[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSelection, setPickerSelection] = useState<string[]>([]);
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    relationshipCashflowApi
      .listScenarios(profileId)
      .then((list) => {
        if (!cancelled) setAllScenarios(list);
      })
      .catch(() => {
        if (!cancelled) setAllScenarios([]);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const openPicker = useCallback(() => {
    setPickerSelection(
      readDetails(proposal).cashflowScenarioIds ?? scenarios.map((s) => s.scenarioId),
    );
    setPickerOpen(true);
  }, [proposal, scenarios]);

  const toggleSelection = useCallback((id: string) => {
    setPickerSelection((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleAttach = useCallback(async () => {
    if (attaching) return;
    setAttaching(true);
    try {
      await onUpdateDetails({ cashflowScenarioIds: pickerSelection });
      const refreshed = await proposalApi.getSections(proposalId);
      const updated = refreshed.find((s) => s.sectionKey === section.sectionKey);
      if (updated) onSaved(updated);
      setPickerOpen(false);
      showToast('Cashflow forecasts attached', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to attach the cashflow forecasts'), 'error');
    } finally {
      setAttaching(false);
    }
  }, [attaching, onUpdateDetails, pickerSelection, proposalId, section.sectionKey, onSaved, showToast]);

  const ai = useAiBlockDraft({
    proposalId,
    profileId,
    sectionKey: section.sectionKey,
    block: 'narrative',
    useCase: 'REPORT_BUILDER',
    buildMessage: (instruction) =>
      `Draft the lifestyle & retirement narrative for proposal ${proposalId} — a short, ` +
      "plain-language paragraph on what these cashflow forecasts mean for the client's lifestyle " +
      'and retirement. Reply with ONLY a JSON object {"narrative": "..."}.' +
      (instruction ? ` Instruction: ${instruction}` : ''),
    buildPayload: (parsed) => {
      const text = replyToNarrative(parsed);
      if (text === null) {
        throw new Error('no usable narrative');
      }
      return { narrative: text };
    },
    onApplied: (updated) => {
      setNarrative(readContent(updated).narrative ?? '');
      setNarrativeDirty(false);
      onSaved(updated);
      showToast('AI draft applied', 'success');
    },
  });

  const handleSaveNarrative = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await proposalApi.updateSection(proposalId, section.sectionKey, {
        content: { ...content, narrative },
      });
      setNarrativeDirty(false);
      onSaved(updated);
      showToast('Lifestyle & Retirement saved', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to save Lifestyle & Retirement'), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, proposalId, section.sectionKey, content, narrative, onSaved, showToast]);

  return (
    <div className="pension-builder__section" data-testid="section-br-lifestyle-retirement">
      {!readOnly && (
        <div className="pension-builder__cashflow-header">
          <h3 className="pension-builder__facts-card-title">Embedded cashflow forecasts</h3>
          <Button
            variant="outlined"
            size="sm"
            onClick={openPicker}
            data-testid="section-br-lifestyle-pick-btn"
          >
            {scenarios.length > 0 ? 'Change forecasts' : 'Choose cashflow model(s)'}
          </Button>
        </div>
      )}

      {scenarios.length === 0 && (
        <p className="pension-builder__empty" data-testid="section-br-lifestyle-empty">
          No cashflow forecasts embedded yet.
        </p>
      )}

      {scenarios.map((scenario) => {
        const chartData = scenario.labels.map((year, i) => {
          const point: Record<string, number> = { year };
          for (const s of scenario.series) point[s.name] = s.values[i];
          return point;
        });
        return (
          <div
            key={scenario.scenarioId}
            className="pension-builder__facts-card"
            data-testid={`section-br-lifestyle-scenario-${scenario.scenarioId}`}
          >
            <h4 className="pension-builder__facts-card-title">{scenario.name}</h4>
            {chartData.length > 0 && scenario.series.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-grey-05)" />
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Legend />
                  {scenario.series.map((s, i) => (
                    <Line
                      key={s.name}
                      type="monotone"
                      dataKey={s.name}
                      name={s.name}
                      stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="pension-builder__empty">No projection data for this forecast.</p>
            )}
          </div>
        );
      })}

      {content.pensionSummary && (
        <div className="pension-builder__facts-card" data-testid="section-br-lifestyle-pension-summary">
          <h4 className="pension-builder__facts-card-title">Pension structures</h4>
          <p>
            {formatCurrency(content.pensionSummary.total, undefined) ?? '—'} across{' '}
            {content.pensionSummary.policyCount ?? 0} polic
            {content.pensionSummary.policyCount === 1 ? 'y' : 'ies'}
          </p>
        </div>
      )}

      {readOnly ? (
        narrative && (
          <TextArea
            label="Narrative"
            value={narrative}
            disabled
            readOnly
            rows={5}
            fluid
            data-testid="section-br-lifestyle-narrative"
          />
        )
      ) : (
        <>
          <AiGenerateRow
            idSuffix={section.sectionKey.toLowerCase()}
            instruction={ai.instruction}
            onInstructionChange={ai.setInstruction}
            onGenerate={() => void ai.generate()}
            generating={ai.generating}
            error={ai.error}
          />
          <TextArea
            label="Narrative"
            value={narrative}
            onChange={(e) => {
              setNarrative(e.target.value);
              setNarrativeDirty(true);
            }}
            placeholder="What these forecasts mean for the client's lifestyle & retirement…"
            rows={5}
            fluid
            data-testid="section-br-lifestyle-narrative"
          />
          <div className="pension-builder__section-footer">
            <Button
              variant="filled"
              onClick={handleSaveNarrative}
              disabled={saving || !narrativeDirty}
              loading={saving}
              data-testid="section-br-lifestyle-save-btn"
            >
              Save
            </Button>
          </div>
        </>
      )}

      {!readOnly && (
      <Modal open={pickerOpen} onClose={() => setPickerOpen(false)} size="medium">
        <Modal.Header title="Choose cashflow model(s)" onClose={() => setPickerOpen(false)} />
        <Modal.Content>
          {allScenarios === null && <Spinner />}
          {allScenarios !== null && allScenarios.length === 0 && (
            <p className="pension-builder__empty" data-testid="section-br-lifestyle-no-scenarios">
              This relationship has no saved cashflow forecasts yet. Model them in the Cashflow
              workspace first, then embed them here.
            </p>
          )}
          {allScenarios !== null && allScenarios.length > 0 && (
            <div data-testid="section-br-lifestyle-scenario-options">
              {allScenarios.map((s) => (
                <Checkbox
                  key={s.id}
                  label={s.name}
                  checked={pickerSelection.includes(s.id)}
                  onChange={() => toggleSelection(s.id)}
                  data-testid={`section-br-lifestyle-scenario-option-${s.id}`}
                />
              ))}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => setPickerOpen(false), disabled: attaching }}
          primaryAction={{
            label: 'Attach',
            onClick: () => {
              void handleAttach();
            },
            disabled: attaching,
            loading: attaching,
          }}
        />
      </Modal>
      )}
    </div>
  );
}

export default SectionLifestyleRetirement;
